import ButtonContainer from 'core/button-container';
import dynamic from 'next/dynamic';
import { fetchSearchData } from 'components/landing/selected-note-list/data-fetcher';
import { memo } from 'react';
import Note from 'components/landing/selected-note-list/note';
import { PinkButton } from 'core/buttons/buttons';
import PropTypes from 'prop-types';
import styles from 'styles/SearchBox.module.scss';
import Title from 'core/title';
import { useTranslations } from 'next-intl';

const LoadingWidget = dynamic(
    () => import('components/landing/selected-note-list/loading-widget')
);
const MessageWidget = dynamic(
    () => import('components/landing/selected-note-list/message-widget')
);

const SearchResultSection = ({ pageCode, searchState }) => {
    const {
        activeSearch,
        loadingSearch,
        errorSearch,
        itemListSearch,
        showMoreButtonSearch,
        currentPageSearch: currentPage,
        searchTerm,
        setData,
        setShowMoreButton,
        setLoading,
        setError,
        setCurrentPageSearch
    } = searchState;
    const t = useTranslations('selected-note-list');

    if (!activeSearch) {
        // Boolean
        return null;
    }

    const handleSearchSuccess = (result) => {
        setData((prev) => [...prev, ...result.results]);
        setShowMoreButton(result.next_page !== null);
        setCurrentPageSearch(currentPage + 1);
    };

    const handleSearchError = () => {
        setError(true);
        setShowMoreButton(false);
    };

    const handleLoadingChange = (isLoading) => {
        setLoading(isLoading);
    };

    // Handle get paginated search data `more`
    const handleClickSearch = () => {
        if (searchTerm !== null) {
            const paramDict = {
                page_code: pageCode,
                search_term: searchTerm,
                is_search: true,
                page: currentPage === null ? 1 : currentPage + 1
            };

            fetchSearchData(
                paramDict,
                handleSearchSuccess,
                handleSearchError,
                handleLoadingChange
            );
        }
    };

    return (
        <>
            {/* Search loading state */}
            {loadingSearch && !errorSearch && <LoadingWidget />}

            {/* Search error state */}
            {!loadingSearch && errorSearch && (
                <MessageWidget message={t('error-message')} />
            )}

            {/* Search results */}
            {itemListSearch && itemListSearch.length > 0 && (
                <>
                    <Title content={t('search-result-title')} />
                    <div className={styles.note_container}>
                        {itemListSearch.map((note, index) => (
                            <Note key={index} item={note} />
                        ))}
                    </div>
                </>
            )}

            {/* Empty search results */}
            {itemListSearch &&
                itemListSearch.length === 0 &&
                !loadingSearch &&
                !errorSearch && <MessageWidget message={t('empty-message')} />}

            {/* Load more button for search */}
            {showMoreButtonSearch &&
                itemListSearch &&
                itemListSearch.length > 0 &&
                (
                    <ButtonContainer>
                        <PinkButton
                            label={t('button.label')}
                            propsButton={{
                                style: {
                                    fontSize: '16px',
                                    fontWeight: 700,
                                    zIndex: 1000,
                                    display: 'flex',
                                    justifyContent: 'center'
                                }
                            }}
                            onClick={handleClickSearch}
                            parentLoading={loadingSearch}
                        />
                    </ButtonContainer>
                )
            }
        </>
    );
};

SearchResultSection.propTypes = {
    pageCode: PropTypes.string.isRequired,
    searchState: PropTypes.shape({
        activeSearch: PropTypes.bool.isRequired,
        loadingSearch: PropTypes.bool.isRequired,
        errorSearch: PropTypes.bool.isRequired,
        itemListSearch: PropTypes.array.isRequired,
        showMoreButtonSearch: PropTypes.bool.isRequired,
        currentPageSearch: PropTypes.number,
        searchTerm: PropTypes.string,
        setData: PropTypes.func.isRequired,
        setShowMoreButton: PropTypes.func.isRequired,
        setLoading: PropTypes.func.isRequired,
        setError: PropTypes.func.isRequired,
        setCurrentPageSearch: PropTypes.func.isRequired
    }).isRequired
};

export default memo(SearchResultSection);
